import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
/* eslint-disable @typescript-eslint/member-ordering */
import { DataType } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { Encoding, EncodingResult, MarkType, VariableType } from '@mode-vegas/vegas';
import { ChartFacade, SelectedChartService } from '@mode/charts/contract';
import { ConditionalFormattingModalComponentFacade, TextFormattingModalService } from '@mode/charts/feature-encoding';
import { EncodingHelpers, FieldFormatHelpers, SwitchSchemaModel, TableEncodingHelpers } from '@mode/charts/util-vegas';
import { ChartExportFacade } from '@mode/file-exports/domain';
import { ChartState } from '@mode/legacy/types-app-state';
import {
  ChartDataTypes,
  ChartTypes,
  ConditionalFormattingTypes,
  FeatureFlag,
  FeatureFlagsFacade,
  FileExportDomain,
  FlamingoTypes,
  SortSettingsTypes,
} from '@mode/shared/contract-common';
import { ConfirmDialogService, SortSettingsModalDialogService } from '@mode/shared/ui';
import {
  CHART_CONVERSION_TABLE,
  DECIMAL_MAX,
  DEFAULT_CHART_FORMAT,
  FLAMINGO_RESERVED_REFS,
  canColTypeUseShortcut,
  canFormatExpressDecimals,
  forceResize,
  getDefaultTextFormat,
  getNewFormatLabelByType,
  resetToShortcut,
  updateAlignment,
  updatePrecision,
  updateTableSizingStyle,
  updateTextWrappingEnabled,
} from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import * as angular from 'angular';
import { isEqual, omit } from 'lodash';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import { ChartExportHelper } from '~/lib/chart-export-helper';
import { ReportRunDataService } from '~/services/report-run-service';
import { AppStore } from '~/types/main.types';
import { ChartObservables } from '~/types/viz-designer.types';
import { MixableBaseController } from '../../../../common/component';
import { quickSortDesc } from '../../../../lib/chart-encoding-helpers';
import { ReportSaveService } from '../../../../services/report-save-service';
import { RouterStoreService } from '../../../../services/router-store-service';

enum BigValueFormatShortcut {
  Default = 'default',
  ShortNumber = 'shortNumber',
  LongNumber = 'longNumber',
  Percent = 'percent',
  Currency = 'currency',
  Financial = 'financial',
}

interface AnalyticsParameters {
  formatShortcutType: ChartTypes.FormatShortcutType;
  bigValueFormatShortcutType: BigValueFormatShortcut;
  columnAlignment: ChartTypes.HorizontalAlignment;
  tableSizingStyleType: ChartTypes.FlatTableSizingStyle;
  newChartType: string;
}

class VizToolbarController extends MixableBaseController {
  static $inject = [
    'RxStoreService',
    'ReportSaveService',
    'ToastService',
    'RouterStoreService',
    'AnalyticsService',
    'ChartFacade',
    'ChartExportFacade',
    'FeatureFlagsFacade',
    'ReportRunDataService',
    'ConfirmDialogService',
    'SortSettingsModalDialogService',
    'ConditionalFormattingModalComponentFacade',
    'TextFormattingModalService',
    'ThoughtspotIntegrationService',
  ];

  // bindings
  private observables: ChartObservables;
  private readOnly?: boolean;
  private objectContext: ChartTypes.ObjectType;
  private vizToken: string;
  private queryToken: string;
  private model: FlamingoTypes.ModeModel;
  private viewName: string;
  private encoding: Encoding;
  private format: ChartTypes.ChartFormat;
  private selectedChart: ChartState;
  private fieldFormats: ChartTypes.FieldFormatMap;
  private chartService: SelectedChartService;
  private activeTableColumn: string;
  private sidebarOpen: boolean;
  private chartData: ChartDataTypes.Dataset;
  private chartTitle: string;
  private onOpenSidebar: () => void;
  private toggleSidebar: () => void;
  private isImportedDataset = false;
  readonly isAnalystStudio = this.thoughtspotIntegrationService.currOrgIntegrated();

  // local state
  private readonly TriggerTypes: typeof ChartTypes.TriggerTypes = ChartTypes.TriggerTypes;
  private readonly allowedChartTypes: ChartTypes.ChartType[] = ['vegas', 'vegasPivotTable'];
  exploreLink: string;
  isReportEditorRoute: boolean;
  isSaving = false;
  saveError = false;
  isPopOverMenuOpen = false;
  sortDescriptionAscending = '';
  sortDescriptionDescending = '';
  grandTotalsTooltip = 'Totals';
  isConversionToVEAllowed = false;
  activeAlignmentIcon: ChartTypes.HorizontalAlignment;
  conditionalFormattingTooltip = 'Conditional formatting';
  activeConditionalFormattingIcon = false;
  fieldColors: ChartTypes.FieldColorMap | undefined;

  isAxisCompact = false;
  isCompactAxisAllowed = false;
  isV1ChartsEnabled = false;
  datasetTooLargeForLegacyCharts = false;

  private enableNestedSorting = false;
  private fileExport$: Observable<FileExportDomain.FileExport | undefined>;
  private exportInvalidated$ = new BehaviorSubject(false);
  private fileExportStatus$: Observable<FileExportDomain.ExportStatus | undefined>;
  private chartNotExportable$: Observable<boolean | undefined>;
  private exportOverRowLimit$: Observable<boolean>;
  private exportRowLimit$: Observable<number>;
  private exportOverRowLimitMessage$: Observable<string>;

  constructor(
    private rxStore: AppStore,
    private reportSaveService: ReportSaveService,
    private alertService: ToastService,
    private routerStoreService: RouterStoreService,
    private analyticsService: AnalyticsService,
    private chartFacade: ChartFacade,
    private chartExportFacade: ChartExportFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private reportRunDataService: ReportRunDataService,
    private confirmDialogService: ConfirmDialogService,
    private sortSettingsDialogService: SortSettingsModalDialogService,
    private conditionalFormattingModalComponentFacade: ConditionalFormattingModalComponentFacade,
    private textFormattingModalService: TextFormattingModalService,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {
    super();

    this.featureFlagsFacade
      .asObservable(FeatureFlag.UseTronLegacyCharts)
      .pipe(takeUntil(this.destroyed$), first())
      .subscribe((flag) => {
        this.isV1ChartsEnabled = flag;
      });
  }

  $onInit() {
    /**
     * The current logic seems to be:
     * - Standard report builder chart editor needs to use a special ref id so it doesn't interfere with the
     *   chart in the report builder itself
     * - Other contexts (viz explorer, exploration, exploration explorer) can use the given viz token as the
     *   ref id since there shouldn't be any conflicts.
     */
    const refId =
      this.objectContext === ChartTypes.ObjectType.Visualization ? FLAMINGO_RESERVED_REFS.ChartDesigner : this.vizToken;

    this.fileExport$ = combineLatest([
      this.chartExportFacade.createLatestExport(this.vizToken),
      this.exportInvalidated$, // TODO: handle invalidation in the domain module
    ]).pipe(map(([fileExport, invalidated]) => (!invalidated ? fileExport : undefined)));
    this.fileExportStatus$ = this.fileExport$.pipe(map((fileExport) => fileExport?.status));

    this.reportRunDataService.selectedQueryReportRunState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ modelStates }) => {
        this.datasetTooLargeForLegacyCharts =
          modelStates && modelStates.hasRecord(this.queryToken) && modelStates.getRecord(this.queryToken).oversized;
      });

    this.chartNotExportable$ = combineLatest([
      this.chartExportFacade.createIsChartExportable(this.vizToken, refId),
      this.fileExportStatus$,
    ]).pipe(map(([exportable, status]) => !exportable || status === FileExportDomain.ExportStatus.Init));

    this.exportRowLimit$ = this.featureFlagsFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit);
    this.exportOverRowLimit$ = this.chartExportFacade.createIsTableOverLimit(refId);
    this.exportOverRowLimitMessage$ = this.exportRowLimit$.pipe(
      map(
        (limit) =>
          `<b class="headline">Export to CSV</b><br>Files will be truncated for tables with over ${limit / 1000}k rows`
      )
    );

    this.observables.fieldColors$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fieldColors: ChartTypes.FieldColorMap) => (this.fieldColors = fieldColors));
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (this.routerStoreService.editorRoute()) {
      this.isReportEditorRoute = true;
      this.exploreLink = window.location.origin + this.routerStoreService.reportShowExploreUrl(this.vizToken);
    } else {
      this.isReportEditorRoute = false;
    }

    if (changes['encoding']) {
      this.exportInvalidated$.next(true);
    }

    // Re-compute sort tooltips
    this.setSortTooltipDescriptions();

    this.isAxisCompact = this.format?.xAxis.compact ?? false;
    this.setIsCompactAxisAllowed();

    const schemaModel = SwitchSchemaModel.make(this.model, this.viewName);
    this.isConversionToVEAllowed =
      this.selectedChart &&
      !['vegas', 'table', 'bigValue'].includes(this.selectedChart.chartType) &&
      EncodingHelpers.isSupportedEncodingConversion(
        this.selectedChart.chartType,
        'vegas',
        this.encoding,
        schemaModel,
        this.format
      );

    this.updateActiveColumnAlignment();

    // Show conditional formatting icon if there is atleast one measure column in flat table
    if (this.selectedChart.chartType === 'table') {
      this.activeConditionalFormattingIcon = this.format.flatTable.columns.some((column: any) => {
        const { id, colType } = column;
        const colName = id.slice(1, id.length - 1);
        const isDimension = Switch.Attrs.detectDimension(colName, colType);
        return !isDimension;
      });
    }
  }

  cloneChart() {
    this.trackAnalytics('Duplicate Chart');
    this.chartFacade.duplicateChartInDesigner(this.vizToken);
  }

  clearViz() {
    if (this.selectedChart) {
      this.trackAnalytics('Clear Chart');
      this.chartService.clearChart();
      this.onOpenSidebar();
    }
  }

  clearFilters() {
    if (this.selectedChart) {
      this.trackAnalytics('Clear Filters');
      this.chartService.clearFilters();
    }
  }

  clearSort() {
    if (this.selectedChart) {
      this.trackAnalytics('Clear Sorts');
      this.chartService.clearSorts();
    }
  }

  clearAllFormatting() {
    if (this.selectedChart) {
      this.trackAnalytics('Clear format');
      this.chartService.clearFormatting();
    }
  }

  isClearFormattingDisabled() {
    // legacy big value uses the vegas fieldFormats obj to store the format
    const usesFieldFormats = [...this.allowedChartTypes, 'bigValue'];
    if (usesFieldFormats.indexOf(this.selectedChart?.chartType) !== -1) {
      return !Object.keys(this.fieldFormats).length;
    } else {
      const omitFormats = ['colors', 'flatTable', 'useMuzeRenderer'];
      const formatDefaultValues = omit(DEFAULT_CHART_FORMAT, omitFormats);
      const currentFormat = omit(this.format, omitFormats);
      return isEqual(currentFormat, formatDefaultValues);
    }
  }

  isClearTableFormattingDisabled() {
    let shouldDisableFormat = true;
    Object.keys(this.fieldFormats).forEach((key: string) => {
      const defaultFormat = getDefaultTextFormat(this.fieldFormats[key].paneFormat.default.colType);
      if (!isEqual(this.fieldFormats[key].paneFormat.default, defaultFormat)) {
        shouldDisableFormat = false;
      }
    });
    return isEqual(this.format.flatTable.general, DEFAULT_CHART_FORMAT.flatTable.general) && shouldDisableFormat;
  }

  sortAscending() {
    if (this.selectedChart && this.selectedChart.canSort) {
      this.trackAnalytics('Sort Ascending');
      this.chartService.quickSort(false);
    }
  }

  sortDescending() {
    if (this.selectedChart && this.selectedChart.canSort) {
      this.trackAnalytics('Sort Descending');
      this.chartService.quickSort(true);
    }
  }

  undo() {
    if (this.canUndo) {
      this.trackAnalytics('Back');
      this.chartService.undo(this.selectedChart.token);
    }
  }

  redo() {
    if (this.canRedo) {
      this.trackAnalytics('Forward');
      this.chartService.redo(this.selectedChart.token);
    }
  }

  swapAxes() {
    if (this.selectedChart && this.selectedChart.canSwap) {
      this.trackAnalytics('Swap Axes');
      this.chartService.swapAxes();
    }
  }

  private setSortTooltipDescriptions() {
    this.sortDescriptionAscending = this.getSortTooltipDescription(false);
    this.sortDescriptionDescending = this.getSortTooltipDescription(true);
  }

  private getSortTooltipDescription(descending: boolean) {
    if (this.selectedChart && this.model) {
      const view = Switch.Views.find(this.model, this.viewName);
      return quickSortDesc(this.selectedChart, view, descending, this.enableNestedSorting);
    } else {
      return `Sort ${descending ? 'Descending' : 'Ascending'}`;
    }
  }

  exportCsv() {
    if (this.chartData) {
      ChartExportHelper.saveAs(ChartExportHelper.makeCsv(this.chartData), this.chartTitle);
    }
  }

  exportTableCsv(fileExportStatus: string) {
    this.chartExportFacade.openDrawer();
    if (fileExportStatus === FileExportDomain.ExportStatus.Error) {
      this.retryCsv();
    } else {
      this.flamingoCsv();
    }
  }

  flamingoCsv() {
    this.exportInvalidated$.next(false);
    this.chartExportFacade.exportDesignerChartCsv(
      this.vizToken,
      this.objectContext === ChartTypes.ObjectType.Visualization ? FLAMINGO_RESERVED_REFS.ChartDesigner : this.vizToken
    );
  }

  retryCsv() {
    this.fileExport$.pipe(first()).subscribe({
      next: (lastExport) => {
        if (lastExport && lastExport.status === FileExportDomain.ExportStatus.Error) {
          this.chartExportFacade.retryExport(lastExport.id);
        }
      },
    });
  }

  downloadCsv() {
    this.fileExport$.pipe(first()).subscribe({
      next: (lastExport) => {
        if (lastExport) {
          this.chartExportFacade.downloadChartDesignerCsv(lastExport.id);
        }
      },
    });
  }

  /** Table Specific Methods */
  /** Basic abstraction of viz type groups until a more robust solution is needed with more additions */
  get vizTypeGroup() {
    switch (this.selectedChart && this.selectedChart.chartType) {
      case 'table':
        return 'table';
      case 'bigValue':
        return 'bigValue';
      default:
        return 'chart';
    }
  }

  /** Is table empty (also returns true if encoding is not yet/no longer present) */
  get isTableEmpty() {
    return this.encoding ? this.encoding && this.encoding.marks.properties.text.values.length === 0 : true;
  }

  /** Get currently selected column or return the table's first column if none is selected */
  get columnToSort(): string {
    return this.activeTableColumn ? this.activeTableColumn : this.encoding.marks.properties.text.values[0].formula;
  }

  toggleSortSettingsModal() {
    // open sort settings modal
    const sortsMap = this.encoding.sorts.reduce((acc: any, sort: any) => {
      acc.set(sort.source, { descending: sort.descending });
      return acc;
    }, new Map());

    const activeColumnSort = sortsMap.get(this.activeTableColumn);

    this.sortSettingsDialogService.open({
      encoding: this.encoding,
      selectedColumn: this.activeTableColumn,
      descending: activeColumnSort ? activeColumnSort.descending : true,
      applyAction: (sorts: SortSettingsTypes.Sort[]) => {
        this.chartService.dispatchEncodingUpdate(
          sorts.reduce(
            (encResult, sort) =>
              TableEncodingHelpers.addSort(encResult.encoding, sort.selected.value, sort.descending, false),
            TableEncodingHelpers.clearSorts(this.encoding)
          )
        );
      },
    });
  }

  toggleConditionalFormattingModal() {
    // open conditional formatting modal
    if (this.isTableEmpty || !this.activeConditionalFormattingIcon) {
      return;
    }
    this.conditionalFormattingModalComponentFacade.open({
      observables: this.observables,
      encoding: this.encoding,
      selectedColumn: this.activeTableColumn,
      fieldColors: this.fieldColors,
      format: this.format,
      applyAction: (colors: ConditionalFormattingTypes.Color[]) => {
        let selectedColors = colors.reduce((acc, color) => ({ ...acc, [color.selected.value]: color }), {});

        const encodingResult = EncodingHelpers.loadEncoding(this.encoding);
        const formatColumns = this.format.flatTable.columns.map((col) => {
          if (selectedColors[col.id]) {
            return {
              ...col,
              conditionalFormatting: {
                isEnabled: true,
                legendId: selectedColors[col.id].legendId,
                color: selectedColors[col.id].color,
                addToText: selectedColors[col.id].addToText,
                selectedColorPalette: selectedColors[col.id].selectedColorPalette,
              },
            };
          } else {
            return {
              ...col,
              conditionalFormatting: {
                isEnabled: false,
              },
            };
          }
        });

        this.chartService.dispatchEncodingAndFormatUpdate(encodingResult, undefined, undefined, {
          ...this.format,
          flatTable: {
            ...this.format.flatTable,
            columns: formatColumns,
          },
        });
      },
    });
  }

  /** Table sort tooltips */
  sortTableTooltip(descending: boolean) {
    if (this.isTableEmpty) {
      return 'Add some columns before you can sort';
    }

    let columnName = this.columnToSort;
    columnName = columnName.slice(1, columnName.length - 1);

    return `Sort "${columnName}" ${descending ? 'descending' : 'ascending'}`;
  }

  /** Sort table by column */
  sortTable(descending: boolean) {
    this.trackAnalytics(descending ? 'Sort Table Descending' : 'Sort Table Ascending');
    const encResult = TableEncodingHelpers.addSort(this.encoding, this.columnToSort, descending);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  /** Clear table to empty encoding state */
  clearTable() {
    this.trackAnalytics('Clear Table');
    const encResult = TableEncodingHelpers.initEmpty();
    if (!this.isClearTableFormattingDisabled()) {
      this.clearTableFormatting();
    }
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  /** Reset table to the initialized encoding state */
  resetTable() {
    // This is now redundant since tables don't start filled anymore
    this.trackAnalytics('Reset Table');
    const encResult = TableEncodingHelpers.initEmpty();
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  /** Clear all sorts from table encoding */
  clearTableSort() {
    this.trackAnalytics('Clear Table Sort');
    const encResult = TableEncodingHelpers.clearSorts(this.encoding);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  clearTableFilters() {
    this.trackAnalytics('Clear Table Filters');
    const encResult = TableEncodingHelpers.clearFilters(this.encoding);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  clearTableFormatting() {
    if (this.selectedChart) {
      this.trackAnalytics('Clear Table Formatting');
      this.chartService.clearFormatting();
    }
  }

  /**
   * Helper to get format for the active table column.
   */
  get activeTableColumnFieldFormat() {
    if (this.activeTableColumn && this.fieldFormats) {
      const fieldFormat = this.fieldFormats[this.activeTableColumn];
      const encField = EncodingHelpers.findInEncoding(this.encoding, this.activeTableColumn);
      const view = this.model ? Switch.Views.find(this.model, this.viewName) : undefined;
      if (encField && view) {
        const compiled = CompiledField.fromEncField(view, encField);
        return FieldFormatHelpers.resolveTextFormatForCompiledField(
          compiled,
          fieldFormat?.paneFormat[ChartTypes.FieldPaneTypes.Default]
        );
      }
      return fieldFormat ? fieldFormat.paneFormat[ChartTypes.FieldPaneTypes.Default] : null;
    } else {
      return null;
    }
  }

  get activeTableColumnDataType() {
    if (this.activeTableColumn && this.model) {
      const view = this.model ? Switch.Views.find(this.model, this.viewName) : undefined;
      if (view) {
        const compiled = CompiledField.fromFormula(view, this.activeTableColumn, Attr.Type.Discrete);
        return compiled.formulaDataType;
      }
    }
  }

  /**
   * Set the format of a big value to the given shortcut preset.
   */
  setValueFormatToShortcut(numberFormat: BigValueFormatShortcut) {
    let textFormat: Partial<ChartTypes.TextFormat>;
    switch (numberFormat) {
      case BigValueFormatShortcut.Default:
        textFormat = {
          type: ChartTypes.FormatType.Default,
          precision: undefined,
          negativeBehavior: undefined,
        };
        break;
      case BigValueFormatShortcut.LongNumber:
        textFormat = {
          type: ChartTypes.FormatType.Number,
          precision: 2,
          negativeBehavior: 'minusSign',
        };
        break;
      case BigValueFormatShortcut.ShortNumber:
        textFormat = {
          type: ChartTypes.FormatType.Number,
          precision: 0,
          negativeBehavior: 'minusSign',
        };
        break;
      case BigValueFormatShortcut.Percent:
        textFormat = {
          type: ChartTypes.FormatType.Percent,
          precision: 2,
          negativeBehavior: 'minusSign',
        };
        break;
      case BigValueFormatShortcut.Currency:
        textFormat = {
          type: ChartTypes.FormatType.Financial,
          precision: 2,
          negativeBehavior: 'minusSign',
        };
        break;
      case BigValueFormatShortcut.Financial:
        textFormat = {
          type: ChartTypes.FormatType.Financial,
          precision: 2,
          negativeBehavior: 'parens',
        };
        break;
    }

    const valueField = this.encoding.marks.properties.text.values[0];
    if (valueField != null) {
      // This block finds the existing default format for the above field
      // which is needed to generate the update format action
      let prevTextFormat: ChartTypes.TextFormat = { type: ChartTypes.FormatType.Default };
      const fieldFormats = this.fieldFormats || {};
      if (fieldFormats[valueField.formula] != null) {
        const fieldFormat = this.fieldFormats[valueField.formula] || FieldFormatHelpers.getDefaultFieldFormat();
        if (fieldFormat.paneFormat[ChartTypes.FieldPaneTypes.Default] != null) {
          prevTextFormat = fieldFormat.paneFormat[ChartTypes.FieldPaneTypes.Default];
        }
      }
      this.trackAnalytics('Set BigValue Format', { bigValueFormatShortcutType: numberFormat });
      this.chartService.updateTextFormat(valueField.formula, ChartTypes.FieldPaneTypes.Default, {
        ...prevTextFormat,
        ...textFormat,
      });
    }
  }

  /**
   * Set the format of a column to the given shortcut preset.
   */
  setColumnFormatToShortcut(shortcutType: ChartTypes.FormatShortcutType) {
    if (this.activeTableColumn && this.canUseColumnShortcut(shortcutType)) {
      this.trackAnalytics('Set Column Format', { formatShortcutType: shortcutType });
      this.updateFieldFormat(
        this.activeTableColumn,
        resetToShortcut(shortcutType, this.activeTableColumn, this.activeTableColumnFieldFormat)
      );
    }
  }

  /**
   * Set the alignment of a column.
   */
  changeColumnAlignment(alignment: ChartTypes.HorizontalAlignment) {
    if (this.activeTableColumn) {
      this.trackAnalytics('Set Column Alignment', { columnAlignment: alignment });
      this.updateFieldFormat(
        this.activeTableColumn,
        updateAlignment(alignment, this.activeTableColumn, this.activeTableColumnFieldFormat)
      );
      this.updateActiveColumnAlignment(alignment);
    }
  }

  /**
   * Change the decimal places of a column.
   */
  changeColumnPrecision(change: number) {
    if (this.activeTableColumn && this.canChangePrecision(change)) {
      this.trackAnalytics('Change Column Precision');
      if (canFormatExpressDecimals(this.activeTableColumnFieldFormat.type)) {
        this.updateFieldFormat(this.activeTableColumn, updatePrecision(change, this.activeTableColumnFieldFormat));
      } else {
        // This will check if the column's type is a number
        // and will first update the format of the short number
        // to allow of us to add/remove decimal buttons as needed
        switch (this.activeTableColumnFieldFormat.colType) {
          case DataType.Name.Float:
          case DataType.Name.Integer:
            if (change > 0) {
              // if the increase button is clicked first we want to format as long number
              this.updateFieldFormat(
                this.activeTableColumn,
                resetToShortcut(
                  ChartTypes.FormatShortcutType.LongNumber,
                  this.activeTableColumn,
                  this.activeTableColumnFieldFormat
                )
              );
            } else {
              // if decrease is clicked first we want to format as short number
              this.updateFieldFormat(
                this.activeTableColumn,
                updatePrecision(
                  change,
                  resetToShortcut(
                    ChartTypes.FormatShortcutType.ShortNumber,
                    this.activeTableColumn,
                    this.activeTableColumnFieldFormat
                  )
                )
              );
            }

            break;
          default:
            return false;
        }
      }
    }
  }

  /**
   * Change the table sizing style
   */
  setTableSizingStyle(styleType: ChartTypes.FlatTableSizingStyle) {
    this.trackAnalytics('Set Table Sizing', { tableSizingStyleType: styleType });
    this.updateFormat(updateTableSizingStyle(styleType, this.format));
  }

  /**
   * Helper to determine if a shortcut should be enabled for the active column.
   */
  canUseColumnShortcut(shortcutType: ChartTypes.FormatShortcutType) {
    if (!this.activeTableColumn || !this.activeTableColumnFieldFormat) {
      return false;
    }

    return canColTypeUseShortcut(shortcutType, this.activeTableColumnFieldFormat.colType);
  }

  /**
   * Helper to determine disabled class/functionality for precision buttons.
   */
  canChangePrecision(change: number) {
    if (!this.activeTableColumnFieldFormat) {
      return false;
    }

    if (canFormatExpressDecimals(this.activeTableColumnFieldFormat.type)) {
      const currentDecimalPlaces = this.activeTableColumnFieldFormat.precision,
        newDecimalPlaces = currentDecimalPlaces + change;

      if (newDecimalPlaces <= DECIMAL_MAX && newDecimalPlaces >= 0) {
        return true;
      }
    } else {
      // If table column is still of a number type it will default
      // to "Plain Text", this allows for change precision to
      // accurately allow us to enable the precision buttons
      switch (this.activeTableColumnFieldFormat.colType) {
        case DataType.Name.Float:
        case DataType.Name.Integer:
          return true;
        default:
          return false;
      }
    }

    return false;
  }

  /**
   * Open the advanced formatting modal.
   */
  openAdvancedFormattingModal() {
    this.openTextFormattingModal();
  }

  openTextFormattingModal() {
    const activeColumnTextFormat = this.activeTableColumnFieldFormat;
    const activeTableColumnDataType = this.activeTableColumnDataType;
    if (this.activeTableColumn && activeTableColumnDataType) {
      this.textFormattingModalService.open({
        title: this.activeTableColumn.slice(1, -1),
        colType: activeTableColumnDataType,
        format: activeColumnTextFormat,
        encoding: this.encoding,
        applyCustomFormat: ({ textFormat }) => {
          this.updateFieldFormatFromModal({
            formula: this.activeTableColumn,
            textFormat,
          });
        },
      });
    }
  }

  /**
   * Get the format dropdown label according to selected column.
   *
   * Check if the shortcutType has been explicitly set to plainText, because in this case
   * we need to set the dropdown label regardless of the formatter type as date fields will
   * not actually be using the default formatter.
   * If we need to handle more cases here in the future, this should be refactored to handle
   * this case in a more robust manner.
   */
  getDropdownLabel() {
    if (this.activeTableColumn && this.activeTableColumnFieldFormat) {
      if (this.activeTableColumnFieldFormat.shortcutType === ChartTypes.FormatShortcutType.PlainText) {
        return 'Plain text';
      } else if (this.activeTableColumnFieldFormat.shortcutType === ChartTypes.FormatShortcutType.Custom) {
        return 'Custom';
      } else {
        return getNewFormatLabelByType(this.activeTableColumnFieldFormat.type);
      }
    } else {
      return 'Format';
    }
  }

  /**
   * Get the sizing dropdown label according to sizing state state
   */
  getSizingDropdownLabel() {
    if (this.format) {
      switch (this.format.flatTable.general.sizingStyle) {
        case 'sizeToFit':
          return 'Size to fit';
        case 'custom':
          return 'Manually size';
      }
    }
    return 'Sizing style';
  }

  /**
   * Helper to save chart format.
   */
  updateFormat(format: ChartTypes.ChartFormat) {
    this.chartService.updateChartFormat(format);
  }

  /**
   * Update text format changes from modal.
   */
  updateFieldFormatFromModal(args: { formula: string; textFormat: ChartTypes.TextFormat }) {
    this.updateFieldFormat(args.formula, args.textFormat);
  }

  /**
   * Helper to save chart fieldFormat.
   */
  updateFieldFormat(formula: string, textFormat: ChartTypes.TextFormat) {
    this.chartService.updateTextFormat(formula, ChartTypes.FieldPaneTypes.Default, textFormat);
  }

  onToggleGrandTotals(encResult: EncodingResult) {
    this.trackAnalytics('Toggle Grand Totals');
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  setIsCompactAxisAllowed() {
    // Handle possible null encoding
    if (this.encoding === null || this.encoding === undefined) {
      this.isCompactAxisAllowed = false;
      return;
    }
    // We can't compact a text mark in a tabular layout
    if (this.encoding.marks.channel === '*' && this.encoding.marks.properties.type.value === MarkType.Text) {
      this.isCompactAxisAllowed = false;
      return;
    }
    // Must have at least one discrete axis
    const axisFields = [this.encoding.x[0], this.encoding.y[0]];
    this.isCompactAxisAllowed = axisFields.some((field) => {
      if (field == null) {
        return false;
      }
      return field.variable.type === VariableType.DISCRETE;
    });
  }

  onAxisFitChange(compact: boolean) {
    const updatedFormat: ChartTypes.ChartFormat = {
      ...this.format,
      xAxis: {
        ...this.format.xAxis,
        compact,
      },
    };
    this.trackAnalytics('Set Axis Fit');
    this.updateFormat(updatedFormat);
  }

  toggleSettingsSidebar() {
    forceResize();
    this.toggleSidebar();
  }

  updateChartType(type: ChartTypes.ChartType | 'legacyChart') {
    if (type === 'legacyChart' || type === 'pivotTable') {
      this.createNewLegacyChart(type);
      return;
    }

    // Check if this will clear the encoding
    const schemaModel = SwitchSchemaModel.make(this.model, this.viewName);
    const isSupportedConversion = EncodingHelpers.isSupportedEncodingConversion(
      this.selectedChart.chartType,
      type,
      this.encoding,
      schemaModel,
      this.format
    );

    if (!isSupportedConversion) {
      // When the conversion is unsupported, display a message dialog
      // giving the user the opportunity to cancel
      const prettyChartName = CHART_CONVERSION_TABLE[type];
      const warningMsg = `Mode does not support conversion for this visualization type. Continuing to ${prettyChartName} will clear the current chart.`;

      this.confirmDialogService.open({
        dialogTitle: 'Chart will be cleared',
        dialogText: warningMsg,
        confirmText: 'Continue & clear chart',
        confirmAction: this.continueUpdateChartType.bind(this, type),
        confirmButtonColor: 'alert',
        cancelAction: this.cancelUpdateChartType.bind(this, type),
        icon: 'icon-warning',
      });
    } else {
      this.continueUpdateChartType(type);
    }
  }

  continueUpdateChartType(type: ChartTypes.ChartType) {
    this.trackAnalytics('Change Chart Type', { newChartType: type });
    this.chartService.updateChartType(type);
  }

  cancelUpdateChartType(type: ChartTypes.ChartType) {
    // We don't need to do anything, but tracking how often users cancel
    // might be interesting.
    this.trackAnalytics('Cancel Change Chart Type', { newChartType: type });
  }

  createNewLegacyChart(type: 'legacyChart' | 'pivotTable') {
    this.reportSaveService.createEmptyChart({
      queryToken: this.queryToken,
      defaultType: type === 'pivotTable' ? 'pivotTable' : undefined,
    });
  }

  toggleTextWrapping(): void {
    if (!this.activeTableColumn || !this.activeTableColumnFieldFormat) {
      return;
    }

    this.updateFieldFormat(this.activeTableColumn, updateTextWrappingEnabled(this.activeTableColumnFieldFormat));
  }

  get activeTableColumnTextWrappingEnabled(): boolean {
    return this.activeTableColumnFieldFormat?.textWrappingEnabled;
  }

  get canUndo(): boolean {
    return this.selectedChart?.canUndo;
  }

  get canRedo(): boolean {
    return this.selectedChart?.canRedo;
  }

  private trackAnalytics(action: string, parameters?: Partial<AnalyticsParameters>) {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      user_experience_id: this.analyticsService.userExperienceId,
      location: AnalyticsEvent.SubContext.VizToolBar,
      action,
      context: AnalyticsEvent.Context.Visualization,
      context_id: this.vizToken,
      chartType: this.selectedChart.chartType,
      ...parameters,
    });
  }

  private updateActiveColumnAlignment(alignment: ChartTypes.HorizontalAlignment = null) {
    if (this.fieldFormats[this.activeTableColumn]) {
      this.activeAlignmentIcon =
        alignment || this.activeTableColumnFieldFormat?.horizontalAlignment || ChartTypes.HorizontalAlignment.Left;
    } else {
      this.activeAlignmentIcon = null;
      this.activeTableColumn = null;
    }
  }
}

export const VizToolbarComponent: angular.IComponentOptions = {
  bindings: {
    observables: '<',
    readOnly: '<',
    objectContext: '<',
    queryToken: '<',
    vizToken: '<',
    history: '<',
    model: '<',
    viewName: '<',
    encoding: '<',
    selectedChart: '<',
    format: '<',
    fieldFormats: '<',
    activeTableColumn: '<',
    chartService: '<',
    sidebarOpen: '<',
    onOpenSidebar: '&',
    toggleSidebar: '&',
    hideGrandTotals: '<',
    hideVisualExplorerToggle: '<',
    hideBackFowardAction: '<',
    hideTableActions: '<',
    chartData: '<',
    chartTitle: '<',
    isImportedDataset: '<',
  },
  controller: VizToolbarController,
  controllerAs: 'ctrl',
  template: `
    <div class="viz-toolbar">
      <div class="button-group" ng-if="!ctrl.hideBackFowardAction">
        <button class="viz-toolbar-button"
          aria-label="Undo"
          uib-tooltip="Undo"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.undo()"
          ng-class="{ disabled: !ctrl.canUndo }"
        >
          <capra-icon icon="icon-control-edit-back"></capra-icon>
        </button>
        <button class="viz-toolbar-button"
          aria-label="Redo"
          uib-tooltip="Redo"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.redo()"
          ng-class="{ disabled: !ctrl.canRedo }"
        >
          <capra-icon icon="icon-control-edit-forward"></capra-icon>
        </button>
      </div>
      <div ng-if="!ctrl.readOnly && !ctrl.hideTableActions" class="button-group">
        <button class="viz-toolbar-button"
          aria-label="Duplicate chart"
          ng-if="!ctrl.readOnly && ctrl.objectContext === 'visualization'"
          uib-tooltip="Duplicate chart"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.cloneChart()"
        >
          <capra-icon icon="icon-control-duplicate-chart"></capra-icon>
        </button>
        <span ng-if="ctrl.vizTypeGroup === 'chart' || ctrl.vizTypeGroup === 'bigValue'" uib-dropdown class="viz-toolbar-button split-button">
          <button class="split-left"
            uib-tooltip="Clear chart"
            aria-label="Clear chart"
            tooltip-append-to-body="true"
            tooltip-placement="bottom"
            ng-click="ctrl.clearViz()"
          >
            <capra-icon icon="icon-control-clear-chart"></capra-icon>
          </button>
          <button class="split-right" uib-dropdown-toggle>
            <capra-icon icon="{{ctrl.isAnalystStudio ? 'icon-chevron-mini': 'icon-caret-mini'}}"></capra-icon>
          </button>
          <ul uib-dropdown-menu>
            <li><a href="#" ng-click="ctrl.clearViz()">Clear chart</a></li>
            <li class="divider"></li>
            <li ng-class="{'disabled':!ctrl.encoding.filter.length}"><a href="#" ng-click="ctrl.clearFilters()">Clear filters</a></li>
            <li ng-class="{'disabled':!ctrl.encoding.sorts.length}"><a href="#" ng-click="ctrl.clearSort()">Clear sort</a></li>
            <li ng-class="{'disabled': ctrl.isClearFormattingDisabled()}"><a href="#" ng-click="ctrl.clearAllFormatting()">Clear format</a></li>
          </ul>
        </span>
        <span ng-if="ctrl.vizTypeGroup === 'table' && !ctrl.hideTableActions" uib-dropdown class="viz-toolbar-button split-button">
          <button class="split-left"
            uib-tooltip="Clear table"
            aria-label="Clear table"
            tooltip-append-to-body="true"
            tooltip-placement="bottom"
            ng-click="ctrl.clearTable()"
          >
            <capra-icon icon="icon-control-clear-chart"></capra-icon>
          </button>
          <button class="split-right" uib-dropdown-toggle>
            <capra-icon icon="{{ctrl.isAnalystStudio ? 'icon-chevron-mini': 'icon-caret-mini'}}"></capra-icon>
          </button>
          <ul uib-dropdown-menu>
            <li><a href="#" ng-click="ctrl.clearTable()">Clear table</a></li>
            <li class="divider"></li>
            <li ng-class="{'disabled':!ctrl.encoding.filter.length}"><a href="#" ng-click="ctrl.clearTableFilters()">Clear filters</a></li>
            <li ng-class="{'disabled':!ctrl.encoding.sorts.length}"><a href="#" ng-click="ctrl.clearTableSort()">Clear sorts</a></li>
            <li ng-class="{'disabled': ctrl.isClearTableFormattingDisabled()}"><a href="#" ng-click="ctrl.clearTableFormatting()">Clear format</a></li>
          </ul>
        </span>
      </div>
      <div class="button-group" ng-if="ctrl.vizTypeGroup === 'chart' || ctrl.vizTypeGroup === 'bigValue'">
        <button class="viz-toolbar-button"
          aria-label="Swap axes"
          ng-if="ctrl.vizTypeGroup !== 'bigValue'"
          uib-tooltip="Swap axes"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.swapAxes()"
          ng-class="{ disabled: !ctrl.selectedChart.canSwap }"
        >
          <capra-icon icon="icon-control-swap-axis"></capra-icon>
        </button>
        <div class="axis-fit-button" ng-if="ctrl.selectedChart.chartType==='vegas'">
          <div
            uib-tooltip="Change display of discrete axis"
            tooltip-append-to-body="true"
            tooltip-placement="bottom"
          >
            <mode-axis-fit
              [disabled]="!ctrl.isCompactAxisAllowed"
              [compact]="ctrl.isAxisCompact"
              (compact-change)="ctrl.onAxisFitChange($event)"
            ></mode-axis-fit>
          </div>
        </div>
        <div class="totals-toolbar-button" ng-if="!ctrl.hideGrandTotals && ctrl.allowedChartTypes.indexOf(ctrl.selectedChart.chartType) !== -1">
          <div
            class="totals-toolbar-button__hover-target"
            uib-tooltip-html="ctrl.grandTotalsTooltip"
            tooltip-class="html-tooltip"
            tooltip-append-to-body="true"
            tooltip-placement="bottom"
          >
            <mode-totals-button
              [encoding]="ctrl.encoding"
              [model]="ctrl.model"
              [view]="ctrl.viewName"
              (encoding-change)="ctrl.onToggleGrandTotals($event)"
            ></mode-totals-button>
          </div>
        </div>
        <button class="viz-toolbar-button"
          aria-label="{{ ctrl.sortDescriptionAscending }}"
          uib-tooltip="{{ ctrl.sortDescriptionAscending }}"
          tooltip-class="tooltip-capra-regular"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.sortAscending()"
          ng-class="{ disabled: !ctrl.selectedChart.canSort }"
        >
          <capra-icon icon="icon-control-sort-ascending-medium"></capra-icon>
        </button>
        <button class="viz-toolbar-button"
          aria-label="{{ ctrl.sortDescriptionDescending }}"
          uib-tooltip="{{ ctrl.sortDescriptionDescending }}"
          tooltip-class="tooltip-capra-regular"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.sortDescending()"
          ng-class="{ disabled: !ctrl.selectedChart.canSort }"
        >
          <capra-icon icon="icon-control-sort-descending-medium"></capra-icon>
        </button>
      </div>
      <!-- PTQC Export csv button - Hide for now while we figure out how to export pivot table into csv -->
      <div class="button-group" ng-if="false">
        <button class="viz-toolbar-button csv-export-btn btn btn-default"
          ng-click="ctrl.exportCsv()"
          aria-label="Export results as .CSV"
          uib-tooltip="Export results as .CSV"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          tooltip-popup-delay="750">
          <strong> Export CSV </strong>
        </button>
      </div>
      <div class="button-group table-sort" ng-if="ctrl.vizTypeGroup === 'table'">
        <button class="viz-toolbar-button"
            uib-tooltip="Advanced sort settings"
            tooltip-append-to-body="true"
            tooltip-placement="bottom"
            aria-label="Advanced sort settings"
            ng-click="ctrl.toggleSortSettingsModal()"
            ng-class="{ disabled: ctrl.isTableEmpty }"
        >
            <capra-icon icon="icon-control-sort-settings"></capra-icon>
        </button>
        <button class="viz-toolbar-button"
          aria-label="{{ ctrl.conditionalFormattingTooltip }}"
          uib-tooltip="{{ ctrl.conditionalFormattingTooltip }}"
          tooltip-class="tooltip-capra-regular"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.toggleConditionalFormattingModal()"
          ng-class="{ disabled: ctrl.isTableEmpty || !ctrl.activeConditionalFormattingIcon }"
        >
          <capra-icon class="small" icon="icon-conditional-formatting"></capra-icon>
        </button>
      </div>
      <div class="button-group table-alignment" ng-if="ctrl.vizTypeGroup === 'table'">
        <div uib-dropdown>
          <div class="viz-toolbar-button split-button" uib-dropdown-toggle>
            <button
              class="split-left"
              uib-tooltip="Align"
              aria-label="Align"
              tooltip-append-to-body="true"
              tooltip-placement="bottom"
              ng-switch="ctrl.activeAlignmentIcon"
            >
              <capra-icon ng-switch-when="left" icon="icon-control-justify-left"></capra-icon>
              <capra-icon ng-switch-when="center" icon="icon-control-justify-center"></capra-icon>
              <capra-icon ng-switch-when="right" icon="icon-control-justify-right"></capra-icon>
              <capra-icon ng-switch-default icon="icon-control-justify-left"></capra-icon>
            </button>
            <button class="split-right">
              <capra-icon icon="{{ctrl.isAnalystStudio ? 'icon-chevron-mini': 'icon-caret-mini'}}"></capra-icon>
            </button>
          </div>
          <div uib-dropdown-menu>
            <div class="dropdown-actions-wrapper">
              <button
                class="viz-toolbar-button sub-button"
                uib-tooltip="Left"
                aria-label="Left"
                tooltip-append-to-body="true"
                tooltip-placement="bottom"
                ng-click="ctrl.changeColumnAlignment('left')"
                ng-class="{ disabled: !ctrl.activeTableColumn, enabled: ctrl.activeAlignmentIcon === 'left' }"
              >
                <capra-icon icon="icon-control-justify-left"></capra-icon>
              </button>
              <button
                class="viz-toolbar-button sub-button"
                uib-tooltip="Center"
                aria-label="Center"
                tooltip-append-to-body="true"
                tooltip-placement="bottom"
                ng-click="ctrl.changeColumnAlignment('center')"
                ng-class="{ disabled: !ctrl.activeTableColumn, enabled: ctrl.activeAlignmentIcon === 'center' }"
              >
                <capra-icon icon="icon-control-justify-center"></capra-icon>
              </button>
              <button
                class="viz-toolbar-button sub-button"
                uib-tooltip="Right"
                aria-label="Right"
                tooltip-append-to-body="true"
                tooltip-placement="bottom"
                ng-click="ctrl.changeColumnAlignment('right')"
                ng-class="{ disabled: !ctrl.activeTableColumn, enabled: ctrl.activeAlignmentIcon === 'right' }"
              >
                <capra-icon icon="icon-control-justify-right"></capra-icon>
              </button>
            </div>
          </div>
        </div>
        <button class="viz-toolbar-button text-wrapping"
          aria-label="Right"
          uib-tooltip="{{ ctrl.activeTableColumnTextWrappingEnabled ? 'Unwrap text' : 'Wrap text' }}"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-class="{ disabled: (!ctrl.activeTableColumn || !ctrl.activeTableColumnFieldFormat), 'enabled': ctrl.activeTableColumnTextWrappingEnabled }"
          ng-click="ctrl.toggleTextWrapping()">
          <capra-icon icon="icon-text-wrapping"></capra-icon>
        </button>
      </div>
      <div class="button-group table-formatting quick-format-table" ng-if="ctrl.vizTypeGroup === 'table'">
        <button class="viz-toolbar-button quick-format-precision-decrease"
          aria-label="Reduce decimal places"
          uib-tooltip="Reduce decimal places"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.changeColumnPrecision(-1)"
          ng-class="{ disabled: !ctrl.canChangePrecision(-1) }"
        >
          <capra-icon icon="icon-control-decrease-decimal"></capra-icon>
        </button>
        <button class="viz-toolbar-button quick-format-precision-increase"
          aria-label="Increase decimal places"
          uib-tooltip="Increase decimal places"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.changeColumnPrecision(1)"
          ng-class="{ disabled: !ctrl.canChangePrecision(1) }"
        >
          <capra-icon icon="icon-control-increase-decimal"></capra-icon>
        </button>
        <button class="viz-toolbar-button quick-format-number"
          aria-label="Number"
          uib-tooltip="Number"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.setColumnFormatToShortcut('shortNumber')"
          ng-class="{ disabled: !ctrl.canUseColumnShortcut('shortNumber') }"
        >
          <capra-icon icon="icon-control-format-number"></capra-icon>
        </button>
        <button class="viz-toolbar-button quick-format-currency"
          aria-label="Currency"
          uib-tooltip="Currency"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.setColumnFormatToShortcut('currency')"
          ng-class="{ disabled: !ctrl.canUseColumnShortcut('currency') }"
        >
          <capra-icon icon="icon-control-format-currency"></capra-icon>
        </button>
        <button class="viz-toolbar-button quick-format-percent"
          aria-label="Percent"
          uib-tooltip="Percent"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.setColumnFormatToShortcut('percent')"
          ng-class="{ disabled: !ctrl.canUseColumnShortcut('percent') }"
        >
          <capra-icon icon="icon-control-format-percent"></capra-icon>
        </button>
        <span uib-dropdown class="viz-toolbar-dropdown new-viz-toolbar-dropdown formats-dropdown">
          <button uib-dropdown-toggle
            aria-label="View format preset options"
            class="dropdown"
          >
            {{ ctrl.getDropdownLabel() }}
            <capra-icon icon="{{ctrl.isAnalystStudio ? 'icon-chevron-mini': 'icon-caret-mini'}}"></capra-icon>
          </button>

          <ul uib-dropdown-menu>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('plainText')"
                ng-disabled="!ctrl.canUseColumnShortcut('plainText')"
              >Plain text<span class="format-example">Normal format</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('url')"
                ng-disabled="!ctrl.canUseColumnShortcut('url')"
              >URL</button>
            </li>
            <li class="divider"></li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('shortNumber')"
                ng-disabled="!ctrl.canUseColumnShortcut('shortNumber')"
              >Short number<span class="format-example">1,006</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('longNumber')"
                ng-disabled="!ctrl.canUseColumnShortcut('longNumber')"
              >Long number<span class="format-example">1,006.00</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('percent')"
                ng-disabled="!ctrl.canUseColumnShortcut('percent')"
              >Percent<span class="format-example">10.62%</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('currency')"
                ng-disabled="!ctrl.canUseColumnShortcut('currency')"
              >Currency<span class="format-example">$1,600</span></button>
            </li>
            <li class="divider"></li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('monthYear')"
                ng-disabled="!ctrl.canUseColumnShortcut('monthYear')"
              >Mon Year<span class="format-example">Feb 2019</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('yearMonthDate')"
                ng-disabled="!ctrl.canUseColumnShortcut('yearMonthDate')"
              >Year-Mon-Date<span class="format-example">2019-02-28</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setColumnFormatToShortcut('dateAndTime')"
                ng-disabled="!ctrl.canUseColumnShortcut('dateAndTime')"
              >Date and time<span class="format-example">2019-02-28 02:32:34</span></button>
            </li>
            <li class="divider"></li>
            <li>
              <button ng-click="ctrl.openAdvancedFormattingModal()"
                ng-disabled="!ctrl.activeTableColumn"
              >More options...</button>
            </li>
          </ul>
        </span>

      </div>
      <div class="button-group table-formatting quick-format-big-value" ng-if="ctrl.vizTypeGroup === 'bigValue'">
        <button class="viz-toolbar-button quick-format-number"
          aria-label="Number"
          uib-tooltip="Number"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Default}')"
        >
          <capra-icon icon="icon-control-format-number"></capra-icon>
        </button>
        <button class="viz-toolbar-button quick-format-currency"
          aria-label="Currency"
          uib-tooltip="Currency"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Currency}')"
        >
          <capra-icon icon="icon-control-format-currency"></capra-icon>
        </button>
        <button class="viz-toolbar-button quick-format-percent"
          aria-label="Percent"
          uib-tooltip="Percent"
          tooltip-append-to-body="true"
          tooltip-placement="bottom"
          ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Percent}')"
        >
          <capra-icon icon="icon-control-format-percent"></capra-icon>
        </button>
        <span uib-dropdown class="viz-toolbar-dropdown formats-dropdown">
          <button uib-dropdown-toggle
            aria-label="View format preset options"
            class="dropdown"
          >{{ ctrl.getDropdownLabel() }}
            <capra-icon icon="{{ctrl.isAnalystStudio ? 'icon-chevron-mini': 'icon-caret-mini'}}"></capra-icon>
          </button>
          <ul uib-dropdown-menu>
            <li>
              <button ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Default}')"
              >1K<span class="format-example">Auto format</span></button>
            </li>
            <li class="divider"></li>
            <li>
              <button ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.ShortNumber}')"
              >Short number<span class="format-example">1,006</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.LongNumber}')"
              >Long number<span class="format-example">1,006.00</span></button>
            </li>
            <li class="divider"></li>
            <li>
              <button ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Percent}')"
              >Percent<span class="format-example">10.62%</span></button>
            </li>
            <li class="divider"></li>
            <li>
              <button ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Currency}')"
              >Currency<span class="format-example">$1,600</span></button>
            </li>
            <li>
              <button ng-click="ctrl.setValueFormatToShortcut('${BigValueFormatShortcut.Financial}')"
              >Accounting<span class="format-example">($1,600)</span></button>
            </li>
          </ul>
        </span>
      </div>
      <div class="button-group table-formatting with-export" ng-if="ctrl.vizTypeGroup === 'table'">
        <span uib-dropdown class="viz-toolbar-dropdown table-size-dropdown">
          <button uib-dropdown-toggle
            aria-label="View table sizing options"
            class="dropdown"
          >{{ ctrl.getSizingDropdownLabel() }}
            <capra-icon icon="{{ctrl.isAnalystStudio ? 'icon-chevron-mini': 'icon-caret-mini'}}"></capra-icon>
          </button>
          <ul uib-dropdown-menu>
            <li>
              <button ng-click="ctrl.setTableSizingStyle('sizeToFit')">Size to fit</button>
            </li>
            <li>
              <button ng-click="ctrl.setTableSizingStyle('custom')">Manually size</button>
            </li>
          </ul>
        </span>
        <capra-tooltip
          [tooltip-content]="ctrl.exportOverRowLimitMessage$ | async:this"
          tooltip-placement="bottom"
          tooltip-content-is-html="true"
          tooltip-interactive="true"
          tooltip-size="regular"
          [tooltip-is-disabled]="!(ctrl.exportOverRowLimit$ | async:this)"
        >
          <capra-button
            icon="icon-export"
            icon-location="left"
            text="Export to CSV"
            class="download-button"
            size="small"
            color="neutral"
            variant="secondary"
            [disabled]="ctrl.chartNotExportable$ | async:this"
            (clicked)="ctrl.exportTableCsv(ctrl.fileExportStatus$ | async:this)"
          ></capra-button>
        </capra-tooltip>
      </div>
      <div class="right-actions">
        <save-indicator
          ng-if="(!ctrl.readOnly && ctrl.objectContext === 'exploration')">
        </save-indicator>
        <div ng-if="ctrl.readOnly || ctrl.objectContext === 'exploration'" class="button-group">
          <mode-visualization-menu
            [trigger-type]="ctrl.TriggerTypes.Button"
            [overlap-trigger]="false"
            [is-local]="!ctrl.isImportedDataset"
            [chart-type]="ctrl.selectedChart.chartType"
            [is-chart-type-convertible-to-viz-explorer]="ctrl.isConversionToVEAllowed"
            [disable-legacy-viz]="true"
            disabled-legacy-viz-tooltip-text="Chart type not supported."
            [show-v1-chart-option]="ctrl.isV1ChartsEnabled"
            (chart-type-selected)="ctrl.updateChartType($event)">
          </mode-visualization-menu>
        </div>
        <a class="sidebar-toggle"
          ng-if="(ctrl.readOnly || ctrl.objectContext === 'exploration')"
          ng-hide="ctrl.views.datasetTab"
          ng-click="ctrl.toggleSettingsSidebar()">
          <capra-icon icon="icon-collapse-panel" ng-class="ctrl.sidebarOpen ? 'collapse' : 'expand'"></capra-icon>
        </a>
      </div>
      <div ng-if="ctrl.isReportEditorRoute" class="button-group">
        <mode-visualization-menu
          ng-if="!ctrl.hideVisualExplorerToggle"
          [trigger-type]="ctrl.TriggerTypes.Button"
          [overlap-trigger]="false"
          [is-local]="!ctrl.isImportedDataset"
          [chart-type]="ctrl.selectedChart.chartType"
          [is-chart-type-convertible-to-viz-explorer]="ctrl.isConversionToVEAllowed"
          [disable-legacy-viz]="ctrl.datasetTooLargeForLegacyCharts || ctrl.isImportedDataset"
          [disabled-legacy-viz-tooltip-text]="ctrl.datasetTooLargeForLegacyCharts ?
            'Chart type not available.\n Dataset is too large.' :
            'Chart type not supported.'"
          [show-v1-chart-option]="ctrl.isV1ChartsEnabled"
          (chart-type-selected)="ctrl.updateChartType($event)">
        </mode-visualization-menu>
      </div>
    </div>
  `,
};
